import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { openDemoModal } from "../utils/demoModal";
import ExitPopup from "./ExitPopup";
import "./Landing.css";
import CookieBanner from "./CookieBanner";

const Icon = ({ name, ...props }) => {
  const paths = {
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    evaluate: (
      <>
        <path d="M4 7h7M4 12h11M4 17h5" />
        <path d="m17 5 3 3-3 3m3 3-3 3 3 3" />
      </>
    ),
    visibility: (
      <>
        <path d="m3 3 18 18M10.6 5.1A10 10 0 0 1 12 5c5 0 9 5 9 7a8 8 0 0 1-2.2 3.3M6.2 6.3C4.2 7.7 3 10 3 12c0 2 4 7 9 7a9 9 0 0 0 4-.9" />
      </>
    ),
    interview: (
      <>
        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" />
        <path d="M9 11h.01M12 11h.01M15 11h.01" />
      </>
    ),
    code: (
      <>
        <path d="m8 7-5 5 5 5m8-10 5 5-5 5m-2-13-4 16" />
      </>
    ),
    shield: (
      <>
        <path d="m12 3 8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    chart: (
      <>
        <path d="M4 20V10m6 10V4m6 16v-7m6 7H2" />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </>
    ),
    report: (
      <>
        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
        <path d="M14 3v6h6M8 13h8M8 17h5" />
      </>
    ),
    audit: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3M8.5 11l2 2 3.5-3.5" />
      </>
    ),
    people: (
      <>
        <circle cx="9" cy="8" r="3.5" />
        <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M18.5 14a6.5 6.5 0 0 1 3 6" />
      </>
    ),
    briefcase: (
      <>
        <rect x="3" y="7" width="18" height="13" rx="2" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18" />
      </>
    ),
    terminal: (
      <>
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m7 10 3 2-3 2m5 1h5" />
      </>
    ),
    video: (
      <>
        <path d="m23 7-7 5 7 5V7Z" />
        <rect x="1" y="5" width="15" height="14" rx="2" />
      </>
    ),
    warning: (
      <>
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4m0 4h.01" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.8-3.8" />
      </>
    ),
  };

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {paths[name]}
    </svg>
  );
};

const challenges = [
  {
    icon: "clock",
    title: "Manual interview work",
    description:
      "Scheduling, interviewing, and evaluation consume valuable team time.",
  },
  {
    icon: "evaluate",
    title: "Inconsistent evaluation",
    description:
      "Different interviewers can assess the same candidate differently.",
  },
  {
    icon: "visibility",
    title: "Limited interview visibility",
    description:
      "Remote interviews make it harder to verify candidate activity and review what happened.",
  },
];

const workflow = [
  ["Define", "Create the role, skills, weightings, and questions."],
  ["Schedule", "Set interview parameters and send a secure interview link."],
  ["Interview", "AI conducts technical interviews and coding assessments."],
  [
    "Verify",
    "Monitor candidate activity across screen, webcam, and second camera.",
  ],
  ["Evaluate", "Get structured scoring, insights, notes, and reports."],
];

const features = [
  {
    icon: "interview",
    title: "AI Interviews",
    description:
      "Let AI conduct structured technical interviews with intelligent follow-up questions.",
  },
  {
    icon: "code",
    title: "Coding Assessments",
    description:
      "Assess real-world programming skills with integrated coding exercises.",
  },
  {
    icon: "shield",
    title: "AI Proctoring",
    description:
      "Monitor candidate activity across screen, webcam, and second camera.",
  },
  {
    icon: "chart",
    title: "Hiring Insights",
    description:
      "Turn interview responses and evaluations into structured hiring insights.",
  },
];

const useCases = [
  [
    "High-Volume Technical Hiring",
    "Run consistent technical interviews across large candidate pools without adding manual effort.",
  ],
  [
    "Remote Technical Interviews",
    "Conduct structured interviews and monitor candidate activity from anywhere.",
  ],
  [
    "Pre-Screening & Candidate Evaluation",
    "Assess role-specific skills before your team spends time on deeper interview rounds.",
  ],
  [
    "Coding & Technical Assessment",
    "Evaluate programming ability through real-time coding exercises and structured scoring.",
  ],
];

const integrityFeatures = [
  ["Screen Monitoring", "Tracks screen activity and prevents tab switching."],
  ["Webcam Monitoring", "Verifies identity and detects impersonation."],
  ["Second Camera", "Captures full body and workspace view."],
  ["AI Alerts", "Flags suspicious activity in real time."],
];

const securityFeatures = [
  {
    icon: "lock",
    title: "Secure Access",
    description:
      "Role-based access helps control who can view hiring information.",
  },
  {
    icon: "report",
    title: "Interview Records",
    description:
      "Review recordings, candidate activity, evaluation reports, and notes.",
  },
  {
    icon: "audit",
    title: "Audit Trail",
    description:
      "Use recorded evidence and flagged activity for review and verification.",
  },
];

const hiringTeams = [
  {
    icon: "people",
    title: "Talent Acquisition Teams",
    description: "Automate repetitive workflows and move candidates faster.",
  },
  {
    icon: "briefcase",
    title: "Hiring Managers",
    description: "Get structured, role-specific candidate evaluations.",
  },
  {
    icon: "terminal",
    title: "Engineering Teams",
    description: "Assess technical knowledge and coding ability consistently.",
  },
];

const videoId = "jjCWSLFyF3k";
const sampleQuestions = [
  "Can you explain the approach you took to solve this problem?",
  "How would this solution scale with ten million inputs?",
  "What trade-offs did you consider in your design?",
];

const Landing = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [question, setQuestion] = useState("");
  const canvasRef = useRef(null);
  const screenRef = useRef(null);

  // Allow the shared navbar to open this modal from any route.
  useEffect(() => {
    const handleOpenContactModal = () => setIsModalOpen(true);
    const handleCloseContactModal = () => setIsModalOpen(false);
    window.addEventListener("tt:open-contact-modal", handleOpenContactModal);
    window.addEventListener("tt:close-contact-modal", handleCloseContactModal);
    return () => {
      window.removeEventListener(
        "tt:open-contact-modal",
        handleOpenContactModal,
      );
      window.removeEventListener("tt:close-contact-modal", handleCloseContactModal);
    };
  }, []);

  useEffect(() => {
    const targets = document.querySelectorAll(
      ".tt-landing .rv, .tt-landing .stagger",
    );
    if (!("IntersectionObserver" in window)) {
      targets.forEach((target) => target.classList.add("in"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            if (entry.target.classList.contains("report"))
              entry.target.classList.add("in-view");
            entry.target
              .querySelectorAll?.("[data-w]")
              .forEach((bar) =>
                bar.style.setProperty("--meter-width", bar.dataset.w),
              );
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -60px 0px" },
    );
    targets.forEach((target) => observer.observe(target));
    const report = document.getElementById("report");
    if (report) observer.observe(report);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let questionIndex = 0;
    let characterIndex = 0;
    let deleting = false;
    let timeout;

    const type = () => {
      const current = sampleQuestions[questionIndex];
      characterIndex += deleting ? -1 : 1;
      setQuestion(current.slice(0, characterIndex));

      if (!deleting && characterIndex === current.length) {
        deleting = true;
        timeout = window.setTimeout(type, 1700);
        return;
      }
      if (deleting && characterIndex === 0) {
        deleting = false;
        questionIndex = (questionIndex + 1) % sampleQuestions.length;
      }
      timeout = window.setTimeout(type, deleting ? 28 : 48);
    };

    timeout = window.setTimeout(type, 800);
    return () => window.clearTimeout(timeout);
  }, []);

  // Match the reference HTML exactly: split only text nodes into animated
  // word spans, while preserving the .hl wrapper around "Smarter Hiring".
  useEffect(() => {
    document.querySelectorAll(".tt-landing [data-split]").forEach((element) => {
      let index = 0;

      const walk = (node) => {
        [...node.childNodes].forEach((child) => {
          if (child.nodeType === 3) {
            const fragment = document.createDocumentFragment();

            child.textContent.split(/(\\s+)/).forEach((word) => {
              if (!word) return;

              if (/^\\s+$/.test(word)) {
                fragment.appendChild(document.createTextNode(word));
                return;
              }

              const span = document.createElement("span");
              span.textContent = word;
              span.style.animationDelay = `${0.25 + index * 0.07}s`;
              index += 1;
              fragment.appendChild(span);
            });

            child.replaceWith(fragment);
          } else if (child.nodeType === 1) {
            walk(child);
          }
        });
      };

      walk(element);

      element.querySelectorAll(".hl span").forEach((span) => {
        span.classList.add("grad");
      });
    });
  }, []);

  // Hero neural-network canvas — kept equivalent to the reference HTML.
  // The important interaction is the pointer-to-node connection that appears
  // while hovering the hero, plus the same node density, distance, fading,
  // sizes, colours and motion used by the original HTML canvas.
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return undefined;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let frame;
    let width = 0;
    let height = 0;
    let points = [];
    const pointer = { x: -999, y: -999 };
    const ratio = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;

      canvas.width = width * ratio;
      canvas.height = height * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);

      const count = Math.round(Math.min(70, (width * height) / 18000));
      points = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 2 + 1.2,
      }));
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);

      for (let i = 0; i < points.length; i += 1) {
        const point = points[i];

        if (!reducedMotion) {
          point.x += point.vx;
          point.y += point.vy;

          if (point.x < 0 || point.x > width) point.vx *= -1;
          if (point.y < 0 || point.y > height) point.vy *= -1;
        }

        // Match the HTML: the network is intentionally softer behind the
        // central headline and becomes more visible toward the sides.
        const centerDistance = Math.abs(point.x - width / 2) / (width / 2 || 1);
        const fade = Math.min(1, Math.max(0.12, centerDistance * 1.4));

        for (let j = i + 1; j < points.length; j += 1) {
          const other = points[j];
          const dx = point.x - other.x;
          const dy = point.y - other.y;
          const distance = Math.hypot(dx, dy);

          if (distance < 150) {
            context.strokeStyle = `rgba(166,111,255,${
              (1 - distance / 150) * 0.35 * fade
            })`;
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(point.x, point.y);
            context.lineTo(other.x, other.y);
            context.stroke();
          }
        }

        // This is the key hover behaviour from the HTML reference: every
        // nearby node connects directly to the mouse pointer.
        const mouseDistance = Math.hypot(
          point.x - pointer.x,
          point.y - pointer.y,
        );
        if (mouseDistance < 180) {
          context.strokeStyle = `rgba(75,20,152,${
            (1 - mouseDistance / 180) * 0.4
          })`;
          context.lineWidth = 1;
          context.beginPath();
          context.moveTo(point.x, point.y);
          context.lineTo(pointer.x, pointer.y);
          context.stroke();
        }

        context.fillStyle = `rgba(${
          i % 3 ? "166,111,255" : "75,20,152"
        },${0.75 * fade})`;
        context.beginPath();
        context.arc(point.x, point.y, point.r, 0, Math.PI * 2);
        context.fill();
      }

      if (!reducedMotion) {
        frame = window.requestAnimationFrame(draw);
      }
    };

    resize();
    draw();

    const onPointerMove = (event) => {
      const bounds = canvas.getBoundingClientRect();
      pointer.x = event.clientX - bounds.left;
      pointer.y = event.clientY - bounds.top;
    };

    const onPointerLeave = () => {
      pointer.x = -999;
      pointer.y = -999;
    };

    canvas.parentElement?.addEventListener("pointermove", onPointerMove);
    canvas.parentElement?.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", resize);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      canvas.parentElement?.removeEventListener("pointermove", onPointerMove);
      canvas.parentElement?.removeEventListener("pointerleave", onPointerLeave);
    };
  }, []);

  useEffect(() => {
    const screen = screenRef.current;
    if (screen && videoPlaying) screen.classList.add("playing");
  }, [videoPlaying]);

  const playVideo = () => {
    setVideoPlaying(true);
    document.getElementById("see")?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "center",
    });
  };

  const openContact = () => openDemoModal();

  return (
    <main id="top" className="tt-landing">
      <section className="hero">
        <canvas id="net" ref={canvasRef} aria-hidden="true" />
        <div className="orb a" />
        <div className="orb b" />
        <div className="wrap">
          <span className="eyebrow fade-in" style={{ animationDelay: ".1s" }}>
            AI-Powered Interviews
          </span>
          <h1 className="split" data-split>
            AI-Powered Interviews  for{" "}
            <span className="hl">Smarter Hiring</span>
          </h1>
          <p className="lead fade-in" style={{ animationDelay: ".9s" }}>
          Conduct thousands of interviews, assess real skills, and deliver human-level evaluation quality at enterprise scale
          </p>
          <div className="ctas fade-in" style={{ animationDelay: "1.1s" }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={openContact}
              style={{ fontFamily: "inherit", border: 0, cursor: "pointer" }}
            >
              Book a Demo <span className="arr">→</span>
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => document.getElementById("see")?.scrollIntoView({ behavior: "smooth", block: "center" })}>
              <span className="play">
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M0 0 10 5 0 10z" />
                </svg>
              </span>
              Watch Product Demo
            </button>
          </div>
          <div className="pills fade-in" style={{ animationDelay: "1.35s" }}>
            <span className="pill">
              <i />
              Automate Interviews
            </span>
            <span className="pill">
              <i />
              Assess Real Skills
            </span>
            <span className="pill">
              <i />
              Verify Candidate Integrity
            </span>
          </div>
        </div>
      </section>

      {/* <div className="trust">
        <div className="wrap">
          <p>Trusted by teams hiring technical talent</p>
          <div className="marquee">
            <div className="track">
              {["BlackRock", "Volvo Eicher", "Maruti Suzuki", "Stanley Black & Decker", "Schneider Electric", "Checkwriters", "Rediker Software", "Baystate Health",].map((company, index) => (
                <span key={`${company}-${index}`} aria-hidden={index >= 6}>
                  {company}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div> */}

      <section id="challenge">
        <div className="wrap challenge-grid">
          <div className="sec-head rv">
            <span className="eyebrow">The Challenge</span>
            <h2>Technical hiring takes more time than it should.</h2>
          </div>
          <div className="stagger">
            {challenges.map((item) => (
              <article className="pain" key={item.title}>
                <div className="n">
                  <Icon name={item.icon} />
                </div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="how" id="how">
        <div className="wrap">
          <div className="sec-head center rv">
            <span className="eyebrow">How It Works</span>
            <h2>From job description to the interview report.</h2>
          </div>
          <div className="steps stagger">
            <div className="rail">
              <b />
            </div>
            {workflow.map(([title, description], index) => (
              <article className="step" key={title}>
                <div className="dot">{String(index + 1).padStart(2, "0")}</div>
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="see">
        <div className="wrap demo-grid">
          <div className="rv">
            <span className="eyebrow">See Viriqo Hires in Action</span>
            <h2>See Viriqo Hires in action.</h2>
            <p className="lead">
              Watch a short walkthrough of an AI interview — from candidate
              assessment to evaluation.
            </p>
            <button
              className="btn btn-primary"
              type="button"
              onClick={playVideo}
            >
              <span className="play play-light">
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M0 0 10 5 0 10z" />
                </svg>
              </span>
              Watch Demo (2 min)
            </button>
            <div className="tags">
              <span className="tag">AI Interview</span>
              <span className="tag">Coding Assessment</span>
              <span className="tag">AI Evaluation</span>
            </div>
          </div>
          <div className="rv">
            <div className="screen" id="screen" ref={screenRef}>
              <div className="bar">
                <i />
                <i />
                <i />
              </div>
              <div className="stage">
                <div className="panel">
                  <span className="lbl">
                    <i />
                    AI Interviewer
                  </span>
                  <div className="ai-orb" />
                  <div className="wave" id="wave">
                    {Array.from({ length: 18 }, (_, index) => (
                      <b
                        key={index}
                        style={{
                          animationDelay: `${(-0.13 * index).toFixed(2)}s`,
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="panel code">
                  <span className="lbl code-label">solution.py</span>
                  <div>
                    <span className="k">def</span> two_sum(nums, target):
                  </div>
                  <div>&nbsp;&nbsp;seen = {"{}"}</div>
                  <div>
                    &nbsp;&nbsp;<span className="k">for</span> i, n{" "}
                    <span className="k">in</span> enumerate(nums):
                  </div>
                  <div>
                    &nbsp;&nbsp;&nbsp;&nbsp;<span className="k">if</span> target
                    - n <span className="k">in</span> seen:
                  </div>
                  <div>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <span className="k">return</span> [seen[target-n], i]
                  </div>
                  <div>
                    &nbsp;&nbsp;&nbsp;&nbsp;seen[n] = i<span className="cur" />
                  </div>
                  <div className="c"># O(n) time</div>
                </div>
                <div className="q">
                  <small>Next Question</small>
                  <p>{question}</p>
                </div>
                {videoPlaying ? (
                  <iframe
                    className="demo-video"
                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                    title="Viriqo Hires product demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <button
                    className="play-big"
                    type="button"
                    onClick={playVideo}
                    aria-label="Play Viriqo Hires product demo video"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M5 3 21 12 5 21z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="features-section">
        <div className="wrap">
          <div className="sec-head center rv">
            <span className="eyebrow">What Viriqo Hires Does</span>
            <h2>Everything you need for technical interviews.</h2>
          </div>
          <div className="cards stagger">
            {features.map((feature) => (
              <article className="card" key={feature.title}>
                <div className="ic">
                  <Icon name={feature.icon} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="uc" id="use-cases">
        <div className="wrap">
          <div className="sec-head rv">
            <span className="eyebrow">Use Cases</span>
            <h2>Built for the moments that matter in technical hiring.</h2>
          </div>
          <div className="uc-list stagger">
            {useCases.map(([title, description], index) => (
              <article className="uc-item" key={title}>
                <span className="num">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="band">
        <div className="wrap">
          <div className="band-inner rv">
            <span className="ring r1" />
            <span className="ring r2" />
            <div className="band-copy">
              <span className="eyebrow">Make Better Hiring Decisions</span>
              <h2>Turn every interview into hiring evidence.</h2>
              <p className="lead">
                Get clear insights, structured evaluations, and reviewable
                records — all in one place.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate("/features")}
              >
                Explore Hiring Intelligence <span className="arr">→</span>
              </button>
            </div>
            <div
              className="report"
              id="report"
              aria-label="Sample interview report"
            >
              <h4>Interview Report</h4>
              {[
                ["Problem Solving", "88%"],
                ["Code Quality", "76%"],
                ["Communication", "82%"],
                ["System Design", "69%"],
              ].map(([label, score]) => (
                <div className="meter" key={label}>
                  <span>
                    {label}
                    <b>{score}</b>
                  </span>
                  <div>
                    <b data-w={score} style={{ width: 0 }} />
                  </div>
                </div>
              ))}
              <div className="ok">
                <Icon name="shield" />
                Integrity checks recorded
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mid-cta">
        <div className="wrap">
          <div className="box rv">
            <h2>See what Viriqo Hires can do for your hiring team.</h2>
            <p className="lead">
              Get a closer look at the platform and explore how it fits your
              interview workflow.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={openContact}
              style={{ fontFamily: "inherit", border: 0, cursor: "pointer" }}
            >
              Book a Personalized Demo <span className="arr">→</span>
            </button>
          </div>
        </div>
      </div>

      <section className="integ" id="integrity">
        <div className="wrap integ-grid">
          <div className="rv">
            <span className="eyebrow">Interview Integrity</span>
            <h2>Make every remote interview more trustworthy.</h2>
            <p className="lead">
              Monitor the interview environment across screen, webcam, and
              second camera while AI identifies and logs suspicious activity.
            </p>
            <div className="integ-list stagger">
              {integrityFeatures.map(([title, description]) => (
                <div key={title}>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rv">
            <div
              className="monitor"
              aria-label="Interview integrity monitoring illustration"
            >
              <div className="orbit" />
              <div className="orbit o2" />
              <div className="sweep" />
              <div className="core">
                <Icon name="shield" />
              </div>
              <div className="node node-top">
                <Icon name="terminal" />
              </div>
              <div className="node node-right">
                <Icon name="people" />
              </div>
              <div className="node node-bottom">
                <Icon name="video" />
              </div>
              <div className="node node-left alert">
                <Icon name="warning" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="security">
        <div className="wrap">
          <div className="sec-head center rv">
            <span className="eyebrow">Security &amp; Auditability</span>
            <h2>Keep a clear record of every interview.</h2>
            <p className="lead">
            Viriqo Hires records interview activity, stores evaluation reports,
              and keeps interview evidence available for review.
            </p>
          </div>
          <div className="sec-grid stagger">
            {securityFeatures.map((feature) => (
              <article className="sec-card" key={feature.title}>
                <div className="ic">
                  <Icon name={feature.icon} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ind" id="industries">
        <div className="wrap center">
          <div className="rv">
            <span className="eyebrow">Industries</span>
            <h2>Technical hiring across teams and industries.</h2>
          </div>
          <div className="ind-row stagger">
            {[
              "Technology",
              "IT Services",
              "BFSI",
              "E-commerce",
              "Enterprise",
            ].map((industry) => (
              <span className="chip" key={industry}>
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="teams" id="teams">
        <div className="wrap">
          <div className="sec-head center rv">
            <span className="eyebrow">Built for Technical Hiring Teams</span>
            <h2>One platform. Different teams.</h2>
            <p className="lead">
              Every team in your hiring process gets the view it needs — from
              first screen to final decision.
            </p>
          </div>
          <div className="team-grid stagger">
            {hiringTeams.map((team) => (
              <article className="team" key={team.title}>
                <div className="ic">
                  <Icon name={team.icon} />
                </div>
                <h3>{team.title}</h3>
                <p>{team.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="final" id="demo-cta">
        <div className="glow" />

        <div className="wrap rv">
          <h2>
            Ready to make technical interviews{" "}
            <span className="grad">simpler?</span>
          </h2>

          <p className="lead">
            See how Viriqo Hires can help your team automate interviews, assess
            skills, and make more confident hiring decisions.
          </p>

          <div className="ctas">
            <button
              type="button"
              className="btn btn-primary"
              onClick={openContact}
              style={{ fontFamily: "inherit", border: 0, cursor: "pointer" }}
            >
              Book a Demo <span className="arr">→</span>
            </button>
          </div>
        </div>
      </section>

      <ExitPopup blocked={isModalOpen || videoPlaying} />
      <CookieBanner />
    </main>
  );
};

export default Landing;
